import { apiSlice } from './apiSlice';
import { setCartState } from './cartSlice';
import {CART_URL} from "../constants";

export const cartApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getCart: builder.query({
            query: () => ({
                url: CART_URL,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(setCartState(data));
                } catch (error) {
                    console.error("Error fetching cart:", error);
                }
            },
            providesTags: ['Cart'],
            // keepUnusedDataFor: 0, // Removed aggressive cache clearing
        }),
        addToCart: builder.mutation({
            query: (item) => ({
                url: `${CART_URL}/items`,
                method: 'POST',
                body: item,
            }),
            async onQueryStarted(item, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(setCartState(data));
                    // Update the query data for getCart to avoid stale data
                    dispatch(
                        cartApiSlice.util.updateQueryData('getCart', undefined, () => {
                            return data;
                        })
                    );
                } catch (error) {
                    console.error("Error adding item to cart:", error);
                }
            },
            invalidatesTags: ['Cart'],
        }),
        removeFromCart: builder.mutation({
            query: (itemId) => ({
                url: `${CART_URL}/items/${itemId}`,
                method: 'DELETE',
            }),
            async onQueryStarted(itemId, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(setCartState(data));
                    // Update the query data for getCart to avoid stale data
                    dispatch(
                        cartApiSlice.util.updateQueryData('getCart', undefined, () => {
                            return data;
                        })
                    );
                } catch (error) {
                    console.error("Error removing item from cart:", error);
                }
            },
            invalidatesTags: ['Cart'],
        }),
        updateCartItem: builder.mutation({
            query: ({ itemId, ...body }) => ({
                url: `${CART_URL}/items/${itemId}`,
                method: 'PUT',
                body,
            }),
            async onQueryStarted({ itemId, ...body }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(setCartState(data));
                    // Update the query data for getCart to avoid stale data
                    dispatch(
                        cartApiSlice.util.updateQueryData('getCart', undefined, () => {
                            return data;
                        })
                    );
                } catch (error) {
                    console.error("Error updating item in cart:", error);
                }
            },
            invalidatesTags: ['Cart'],
        }),
        updateCartItemVariation: builder.mutation({
            query: ({ itemId, variation }) => ({
                url: `${CART_URL}/item/${itemId}`,
                method: 'PUT',
                body: { variation },
            }),
            async onQueryStarted({ itemId, variation }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(setCartState(data));
                    // Update the query data for getCart to avoid stale data
                    dispatch(
                        cartApiSlice.util.updateQueryData('getCart', undefined, () => {
                            return data;
                        })
                    );
                } catch (error) {
                    console.error("Error updating item variation:", error);
                }
            },
            invalidatesTags: ['Cart'],
        }),
        updateCart: builder.mutation({
            query: (data) => ({
                url: CART_URL,
                method: 'PUT',
                body: data,
            }),
            async onQueryStarted(data, { dispatch, queryFulfilled }) {
                try {
                    const { data: res } = await queryFulfilled;
                    dispatch(setCartState(res));
                    // Update the query data for getCart to avoid stale data
                    dispatch(
                        cartApiSlice.util.updateQueryData('getCart', undefined, () => {
                            return res;
                        })
                    );
                } catch (error) {
                    console.error("Error updating cart:", error);
                }
            },
            invalidatesTags: ['Cart'],
        }),
        validateCartStock: builder.mutation({
            query: (data) => ({
                url: `${CART_URL}/validate-stock`,
                method: 'POST',
                body: data,
            }),
            // invalidatesTags: ['Cart'], // Removed to prevent infinite loops in CartScreen
        }),
        updateActiveOrder: builder.mutation({
            query: (data) => ({
                url: CART_URL,
                method: 'PUT',
                body: data,
            }),
            async onQueryStarted(data, { dispatch, queryFulfilled }) {
                try {
                    const { data: res } = await queryFulfilled;
                    dispatch(setCartState(res));
                    // Update the query data for getCart to avoid stale data
                    dispatch(
                        cartApiSlice.util.updateQueryData('getCart', undefined, () => {
                            return res;
                        })
                    );
                } catch (error) {
                    console.error("Error updating cart:", error);
                }
            },
            invalidatesTags: ['Cart'],
        }),
        clearCartItemsByCompany: builder.mutation({
            query: (companyRuc) => ({
                url: `${CART_URL}/companies/${encodeURIComponent(companyRuc)}`,
                method: 'DELETE',
            }),
            async onQueryStarted(companyRuc, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(setCartState(data));
                    // Update the query data for getCart to avoid stale data
                    dispatch(
                        cartApiSlice.util.updateQueryData('getCart', undefined, () => {
                            return data;
                        })
                    );
                } catch (error) {
                    console.error("Error clearing cart items by company:", error);
                }
            },
            invalidatesTags: ['Cart'],
        }),
        createSharedCart: builder.mutation({
            query: (company) => ({
                url: `${CART_URL}/share`,
                method: 'POST',
                body: { company },
            }),
        }),
        getSharedCart: builder.query({
            query: (token) => ({
                url: `${CART_URL}/share/${token}`,
            }),
        }),
        importSharedCart: builder.mutation({
            query: (token) => ({
                url: `${CART_URL}/share/${token}/import`,
                method: 'POST',
            }),
            invalidatesTags: ['Cart'],
        }),
        applyDiscountToCart: builder.mutation({
            query: ({ code, companyRuc }) => ({
                url: `${CART_URL}/apply-discount`,
                method: 'POST',
                body: { code, companyRuc },
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    const cartData = data?.cart || data;
                    dispatch(setCartState(cartData));
                    dispatch(
                        cartApiSlice.util.updateQueryData('getCart', undefined, () => {
                            return cartData;
                        })
                    );
                } catch (error) {
                    console.error('Error applying discount to cart:', error);
                }
            },
            invalidatesTags: ['Cart'],
        }),
        removeDiscountFromCart: builder.mutation({
            query: ({ companyRuc }) => ({
                url: `${CART_URL}/remove-discount`,
                method: 'POST',
                body: { companyRuc },
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    const cartData = data?.cart || data;
                    dispatch(setCartState(cartData));
                    dispatch(
                        cartApiSlice.util.updateQueryData('getCart', undefined, () => {
                            return cartData;
                        })
                    );
                } catch (error) {
                    console.error('Error removing discount from cart:', error);
                }
            },
            invalidatesTags: ['Cart'],
        }),
    }),
});

export const {
    useGetCartQuery,
    useAddToCartMutation,
    useRemoveFromCartMutation,
    useUpdateCartItemMutation,
    useUpdateCartItemVariationMutation,
    useUpdateCartMutation,
    useValidateCartStockMutation,
    useUpdateActiveOrderMutation,
    useCreateSharedCartMutation,
    useGetSharedCartQuery,
    useImportSharedCartMutation,
    useApplyDiscountToCartMutation,
    useRemoveDiscountFromCartMutation,
    useClearCartItemsByCompanyMutation,
} = cartApiSlice;
